import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle, CheckCircle2, Upload, History } from "lucide-react";
import { HELOC_DATASETS, DEFAULT_HELOC_DATASET_KEY } from "@/data/dscr-heloc-datasets";
import {
  activateHelocPricingVersion,
  importHelocPricingSeed,
  importHelocPricingWorkbook,
  listHelocPricingVersions,
} from "@/lib/dscr-heloc.functions";

export const Route = createFileRoute("/admin/heloc-pricing")({
  head: () => ({
    meta: [
      { title: "HELOC pricing schedule · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: HelocPricingAdmin,
});

type Version = {
  id: string;
  versionLabel: string;
  effectiveDate: string;
  status: string;
  rowCount: number;
  importedAt: string;
  notes: string | null;
};

function HelocPricingAdmin() {
  const list = useServerFn(listHelocPricingVersions);
  const importWorkbook = useServerFn(importHelocPricingWorkbook);
  const importSeed = useServerFn(importHelocPricingSeed);
  const activate = useServerFn(activateHelocPricingVersion);

  const [versions, setVersions] = useState<Version[]>([]);
  const [audit, setAudit] = useState<Array<Record<string, unknown>>>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<unknown>(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [versionLabel, setVersionLabel] = useState("");
  const [datasetKey, setDatasetKey] = useState(DEFAULT_HELOC_DATASET_KEY);
  const fileRef = useRef<HTMLInputElement>(null);

  async function refresh() {
    try {
      const res = (await list({})) as { versions: Version[]; audit: Array<Record<string, unknown>> };
      setVersions(res.versions);
      setAudit(res.audit);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load versions");
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Choose an .xlsx workbook first.");
      return;
    }
    setBusy("upload");
    setError(null);
    setPreview(null);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      let binary = "";
      for (let i = 0; i < buf.length; i += 1) binary += String.fromCharCode(buf[i]!);
      const fileBase64 = btoa(binary);
      const res = await importWorkbook({
        data: {
          filename: file.name,
          fileBase64,
          effectiveDate: effectiveDate || null,
          versionLabel: versionLabel || null,
          notes: null,
        },
      });
      setPreview(res);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setBusy(null);
    }
  }

  async function onSeed() {
    setBusy("seed");
    setError(null);
    setPreview(null);
    try {
      setPreview(await importSeed({ data: { datasetKey } }));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Seed import failed");
    } finally {
      setBusy(null);
    }
  }

  async function onActivate(id: string, rollback: boolean) {
    setBusy(id);
    setError(null);
    try {
      await activate({ data: { versionId: id, rollback } });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Activation failed");
    } finally {
      setBusy(null);
    }
  }

  const activeId = versions.find((v) => v.status === "active")?.id ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Investment property HELOC pricing schedule</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload a future wholesale pricing schedule, review the preview diff, then explicitly activate it. The public
          tool only ever reads the active validated version. Uploaded workbooks are stored privately.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" /> Step 1 · Upload workbook
          </CardTitle>
          <CardDescription>
            Three steps: <strong>Upload workbook</strong> → <strong>Review validation &amp; diff</strong> →{" "}
            <strong>Activate version</strong>. Uploads always stage a draft; nothing goes live until you press
            Activate. Expected tabs: “Non-Primary 1st Lien” and “Non-Primary 2nd Lien”, with 0.00% / 0.99% / 1.99% /
            2.99% origination blocks and 10/15/20/30-year terms. Blank cells stay unavailable, and a malformed
            workbook is rejected with the active version left untouched.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="eff">Effective date (optional)</Label>
              <Input id="eff" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Leave blank to use the effective date printed on the workbook’s pricing sheet.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="label">Version label (optional)</Label>
              <Input
                id="label"
                placeholder="Wholesale pricing schedule YYYY-MM-DD"
                value={versionLabel}
                onChange={(e) => setVersionLabel(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wb">Workbook (.xlsx)</Label>
            <Input id="wb" ref={fileRef} type="file" accept=".xlsx" />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={onUpload} disabled={busy !== null}>
              {busy === "upload" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Parse & stage draft
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="dataset" className="text-xs text-muted-foreground">
                Built-in schedule
              </Label>
              <select
                id="dataset"
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={datasetKey}
                onChange={(e) => setDatasetKey(e.target.value)}
              >
                {HELOC_DATASETS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.versionLabel}
                  </option>
                ))}
              </select>
              <Button variant="outline" onClick={onSeed} disabled={busy !== null}>
                {busy === "seed" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Stage built-in schedule as draft
              </Button>
            </div>
          </div>
          {preview ? <ValidationSummary preview={preview as PreviewResult} /> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Step 3 · Activate version</CardTitle>
          <CardDescription>
            Activate a reviewed draft, or roll back to any previous version. Activation switches the public pricing and
            the “Pricing updated” date atomically — no code change or redeploy is needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {versions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No versions imported yet.</p>
          ) : (
            versions.map((v) => (
              <div
                key={v.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {v.status === "active" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : null}
                    {v.versionLabel}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Effective {v.effectiveDate} · {v.rowCount} rows · {v.status} · imported{" "}
                    {new Date(v.importedAt).toLocaleString()}
                  </div>
                </div>
                {v.id === activeId ? (
                  <span className="text-xs font-semibold text-emerald-700">Active</span>
                ) : (
                  <Button
                    size="sm"
                    variant={v.status === "draft" ? "default" : "outline"}
                    disabled={busy !== null}
                    onClick={() => onActivate(v.id, v.status !== "draft")}
                  >
                    {busy === v.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {v.status === "draft" ? "Activate" : "Roll back to this"}
                  </Button>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> Audit history
          </CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="max-h-72 overflow-auto rounded-lg bg-muted p-3 text-xs">
            {JSON.stringify(audit, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

type PreviewResult = {
  ok: boolean;
  rowCount?: number;
  detectedEffectiveDate?: string | null;
  summary?: {
    firstLienValid: number;
    secondLienValid: number;
    totalValid: number;
    unavailable: number;
  };
  errors?: Array<{ sheet: string; row: number; message: string }>;
  warnings?: Array<{ sheet: string; row: number; message: string }>;
  version?: { id: string; effectiveDate: string; versionLabel: string };
  diff?: {
    added: number;
    removed: number;
    changedCount: number;
    unchanged: number;
    changed?: Array<{ key?: string; cell?: string; from: number | null; to: number | null }>;
  };
};

function ValidationSummary({ preview }: { preview: PreviewResult }) {
  const s = preview.summary;
  const diff = preview.diff;
  const changedSamples = (diff?.changed ?? []).slice(0, 10);

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold">Step 2 · Review validation &amp; diff</h3>

      {preview.ok ? (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Staged as a draft — not live yet</AlertTitle>
          <AlertDescription>
            {preview.version ? (
              <>
                {preview.version.versionLabel} · effective {preview.version.effectiveDate}. Review the counts below,
                then press Activate in the Versions list.
              </>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Rejected — active pricing unchanged</AlertTitle>
          <AlertDescription>This workbook failed validation. Nothing was staged or activated.</AlertDescription>
        </Alert>
      )}

      {s ? (
        <div className="grid gap-2 sm:grid-cols-4">
          {[
            ["1st lien priced cells", s.firstLienValid],
            ["2nd lien priced cells", s.secondLienValid],
            ["Total priced cells", s.totalValid],
            ["Unavailable cells", s.unavailable],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-md bg-muted p-3">
              <div className="text-xs text-muted-foreground">{label}</div>
              <div className="text-lg font-semibold">{value}</div>
            </div>
          ))}
        </div>
      ) : null}

      {preview.detectedEffectiveDate ? (
        <p className="text-xs text-muted-foreground">
          Effective date read from the workbook: {preview.detectedEffectiveDate}
        </p>
      ) : null}

      {diff ? (
        <div className="text-xs text-muted-foreground">
          Diff vs active version — {diff.added} added · {diff.removed} removed · {diff.changedCount} changed ·{" "}
          {diff.unchanged} unchanged
        </div>
      ) : null}

      {changedSamples.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-3">Sample changed cell</th>
                <th className="py-1 pr-3">Was</th>
                <th className="py-1">Now</th>
              </tr>
            </thead>
            <tbody>
              {changedSamples.map((c, i) => (
                <tr key={`${c.key ?? c.cell ?? i}`} className="border-t border-border">
                  <td className="py-1 pr-3">{c.key ?? c.cell}</td>
                  <td className="py-1 pr-3">{c.from == null ? "unavailable" : `${c.from.toFixed(3)}%`}</td>
                  <td className="py-1">{c.to == null ? "unavailable" : `${c.to.toFixed(3)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {(preview.errors ?? []).length > 0 ? (
        <div className="space-y-1">
          <div className="text-xs font-semibold text-destructive">Errors</div>
          <ul className="max-h-48 space-y-1 overflow-auto text-xs text-destructive">
            {(preview.errors ?? []).slice(0, 50).map((e, i) => (
              <li key={i}>
                {e.sheet}
                {e.row ? ` row ${e.row}` : ""}: {e.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {(preview.warnings ?? []).length > 0 ? (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">Warnings ({(preview.warnings ?? []).length})</summary>
          <ul className="mt-1 max-h-40 space-y-1 overflow-auto">
            {(preview.warnings ?? []).slice(0, 50).map((w, i) => (
              <li key={i}>
                {w.sheet}
                {w.row ? ` row ${w.row}` : ""}: {w.message}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
